import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { TableSkeleton } from "../../../components/SkeletonLoader";
import {
  METADATA_TYPE_OPTIONS,
  METADATA_VISIBILITY_OPTIONS,
  MetadataType,
  MetadataVisibility,
  ObjectMetadata,
  OBJECT_METADATA_FRAGMENT,
} from "../shared/customFieldFragments";

const OBJECT_METADATA_QUERY = gql`
  query objectMetadata($objectType: ObjectType!, $objectId: ID!) {
    objectMetadata(objectType: $objectType, objectId: $objectId) {
      ...ObjectMetadataFields
    }
  }
  ${OBJECT_METADATA_FRAGMENT}
`;

const SET_OBJECT_METADATA = gql`
  mutation setObjectMetadata($input: SetObjectMetadataInput!) {
    setObjectMetadata(input: $input) {
      ...ObjectMetadataFields
    }
  }
  ${OBJECT_METADATA_FRAGMENT}
`;

const REMOVE_OBJECT_METADATA = gql`
  mutation removeObjectMetadata($input: RemoveObjectMetadataInput!) {
    removeObjectMetadata(input: $input)
  }
`;

interface ObjectMetadataPanelProps {
  objectType: string;
  objectId: string;
}

interface NewEntry {
  key: string;
  value: string;
  type: MetadataType;
  visibility: MetadataVisibility;
}

const emptyEntry: NewEntry = {
  key: "",
  value: "",
  type: MetadataType.STRING,
  visibility: MetadataVisibility.PUBLIC,
};

export const ObjectMetadataPanel = ({
  objectType,
  objectId,
}: ObjectMetadataPanelProps) => {
  const { data, loading, refetch } = useQuery<{
    objectMetadata: ObjectMetadata[];
  }>(OBJECT_METADATA_QUERY, {
    variables: { objectType, objectId },
  });

  const [setMetadata] = useMutation(SET_OBJECT_METADATA);
  const [removeMetadata] = useMutation(REMOVE_OBJECT_METADATA);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState<NewEntry>({ ...emptyEntry });
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const entries = data?.objectMetadata ?? [];

  const METADATA_KEY_REGEX = /^[a-zA-Z0-9_-]{1,100}$/;

  const validateUrlValue = (value: string): string | null => {
    if (!value) return null;
    try {
      const parsed = new URL(value);
      if (!["https:", "http:"].includes(parsed.protocol)) {
        return "URL must use http:// or https://";
      }
      return null;
    } catch {
      return "Please enter a valid URL";
    }
  };

  const sanitizeError = (error: unknown): string => {
    if (!(error instanceof Error)) return "An unexpected error occurred";
    if (error.message.length < 200) return error.message;
    return "An unexpected error occurred";
  };

  const handleAdd = async () => {
    if (!newEntry.key || !newEntry.value) return;

    if (!METADATA_KEY_REGEX.test(newEntry.key)) {
      notifications.show({
        title: "Invalid key",
        message:
          "Key must be 1-100 characters and contain only letters, numbers, underscores, or hyphens",
        color: "red",
        autoClose: 5000,
      });
      return;
    }

    if (entries.find((e) => e.key === newEntry.key)) {
      notifications.show({
        title: "Duplicate key",
        message: `A metadata entry with key "${newEntry.key}" already exists`,
        color: "orange",
        autoClose: 5000,
      });
      return;
    }

    if (
      (newEntry.type === MetadataType.IMAGE_URL ||
        newEntry.type === MetadataType.URL) &&
      newEntry.value
    ) {
      const urlError = validateUrlValue(newEntry.value);
      if (urlError) {
        notifications.show({
          title: "Invalid URL",
          message: urlError,
          color: "red",
          autoClose: 5000,
        });
        return;
      }
    }

    try {
      await setMetadata({
        variables: {
          input: {
            objectType,
            objectId,
            entries: [
              {
                key: newEntry.key,
                value: newEntry.value,
                type: newEntry.type,
                visibility: newEntry.visibility,
              },
            ],
          },
        },
      }).then((result) => {
        if (result?.error) {
          throw new Error(result.error.message);
        }
      });

      notifications.show({
        title: "Metadata added",
        message: `Metadata "${newEntry.key}" has been added`,
        color: "green",
        autoClose: 3000,
      });

      setNewEntry({ ...emptyEntry });
      setShowAddForm(false);
      refetch();
    } catch (error) {
      notifications.show({
        title: "Failed to add metadata",
        message: sanitizeError(error),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  const handleEditSave = async (entry: ObjectMetadata) => {
    if (
      (entry.type === MetadataType.IMAGE_URL ||
        entry.type === MetadataType.URL) &&
      editValue
    ) {
      const urlError = validateUrlValue(editValue);
      if (urlError) {
        notifications.show({
          title: "Invalid URL",
          message: urlError,
          color: "red",
          autoClose: 5000,
        });
        return;
      }
    }

    try {
      await setMetadata({
        variables: {
          input: {
            objectType,
            objectId,
            entries: [
              {
                key: entry.key,
                value: editValue,
                type: entry.type,
                visibility: entry.visibility,
              },
            ],
          },
        },
      }).then((result) => {
        if (result?.error) {
          throw new Error(result.error.message);
        }
      });

      notifications.show({
        title: "Metadata updated",
        message: `Metadata "${entry.key}" has been updated`,
        color: "green",
        autoClose: 3000,
      });

      setEditingKey(null);
      refetch();
    } catch (error) {
      notifications.show({
        title: "Failed to update metadata",
        message: sanitizeError(error),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  const handleDelete = async (key: string) => {
    const confirmed = await new Promise<boolean>((resolve) =>
      modals.openConfirmModal({
        title: "Delete Metadata",
        children: (
          <Text>
            Are you sure you want to delete the metadata entry &quot;{key}
            &quot;?
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      }),
    );

    if (!confirmed) return;

    try {
      await removeMetadata({
        variables: {
          input: {
            objectType,
            objectId,
            keys: [key],
          },
        },
      }).then((result) => {
        if (result?.error) {
          throw new Error(result.error.message);
        }
      });

      notifications.show({
        title: "Metadata deleted",
        message: `Metadata "${key}" has been deleted`,
        color: "green",
        autoClose: 3000,
      });

      refetch();
    } catch (error) {
      notifications.show({
        title: "Failed to delete metadata",
        message: sanitizeError(error),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  if (loading) {
    return <TableSkeleton rows={3} columns={3} />;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>Object Metadata</Title>
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          variant="light"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? "Cancel" : "Add Metadata"}
        </Button>
      </Group>

      {showAddForm && (
        <Card p="sm" withBorder>
          <Stack gap="xs">
            <Group grow>
              <TextInput
                label="Key"
                value={newEntry.key}
                onChange={(e) =>
                  setNewEntry({ ...newEntry, key: e.target.value })
                }
                placeholder="e.g. discord_id"
                size="sm"
                maxLength={100}
                error={
                  newEntry.key &&
                  !METADATA_KEY_REGEX.test(newEntry.key) &&
                  "Only letters, numbers, underscores, and hyphens allowed"
                }
              />
              <TextInput
                label="Value"
                value={newEntry.value}
                onChange={(e) =>
                  setNewEntry({ ...newEntry, value: e.target.value })
                }
                placeholder="Enter value"
                size="sm"
                maxLength={4096}
              />
            </Group>
            <Group grow>
              <Select
                label="Type"
                data={METADATA_TYPE_OPTIONS}
                value={newEntry.type}
                onChange={(val) =>
                  setNewEntry({
                    ...newEntry,
                    type: (val as MetadataType) ?? MetadataType.STRING,
                  })
                }
                size="sm"
              />
              <Select
                label="Visibility"
                data={METADATA_VISIBILITY_OPTIONS}
                value={newEntry.visibility}
                onChange={(val) =>
                  setNewEntry({
                    ...newEntry,
                    visibility:
                      (val as MetadataVisibility) ?? MetadataVisibility.PUBLIC,
                  })
                }
                size="sm"
              />
            </Group>
            <Group justify="right">
              <Button
                size="xs"
                onClick={handleAdd}
                disabled={!newEntry.key || !newEntry.value}
              >
                Add Entry
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {entries.length === 0 && !showAddForm && (
        <Text c="dimmed" size="sm">
          No metadata entries for this object.
        </Text>
      )}

      {entries.length > 0 && (
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th>Type</th>
              <th>Visibility</th>
              <th style={{ width: 80 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.key}>
                <td>
                  <Text size="sm" ff="monospace">
                    {entry.key}
                  </Text>
                </td>
                <td>
                  {editingKey === entry.key ? (
                    <Group gap="xs" wrap="nowrap">
                      <TextInput
                        size="xs"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{ flex: 1 }}
                        maxLength={4096}
                      />
                      <Button
                        size="compact-xs"
                        onClick={() => handleEditSave(entry)}
                      >
                        Save
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        onClick={() => setEditingKey(null)}
                      >
                        Cancel
                      </Button>
                    </Group>
                  ) : (
                    <Text size="sm" lineClamp={2}>
                      {entry.value}
                    </Text>
                  )}
                </td>
                <td>
                  <Badge size="xs" variant="light">
                    {entry.type}
                  </Badge>
                </td>
                <td>
                  <Badge
                    size="xs"
                    variant="light"
                    color={
                      entry.visibility === MetadataVisibility.PUBLIC
                        ? "green"
                        : "orange"
                    }
                  >
                    {entry.visibility}
                  </Badge>
                </td>
                <td>
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Edit">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="blue"
                        onClick={() => {
                          setEditingKey(entry.key);
                          setEditValue(entry.value);
                        }}
                      >
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Delete">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(entry.key)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  );
};
